import Ajv from "ajv";
import axios from "axios";
import axiosRetry from 'axios-retry';
import urlJoin from "url-join";
import Config from "../../../core/config/Config";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";
import PlatformTokenPair from "../model/PlatformTokenPair";
import PlatformUserModel from "../model/PlatformUserModel";

export default class PlatformUserService {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {Config} config 
     * @param {PlatformUserModel} platformUserModel 
     */
    constructor(dispatcher, config, platformUserModel) {
        this._dispatcher = dispatcher;
        this._config = config;
        this._platformUserModel = platformUserModel;

        this._apiEndpoint = this._config.getValue(NGTConfigKey.PLATFORM_API_ENDPOINT);
        this._apiGameEndpoint = this._config.getValue(NGTConfigKey.PLATFORM_API_GAME_ENDPOINT);
        this._apiVersion = this._config.getValue(NGTConfigKey.PLATFORM_API_VERSION);
        
        this._tokenPair = null;
        this._isRefreshing = false;
        this._refreshSubscribers = [];
    }

    /**
     * @param {PlatformTokenPair} tokenPair 
     */
    setTokenPair(tokenPair) {
        this._tokenPair = tokenPair;
    }

    /**
     * Refreshes the access token using the refresh token.
     * @returns {Promise<PlatformTokenPair>}
     */
    async refreshAccessToken() {
        if (!this._tokenPair || !this._tokenPair.refreshToken) {
            throw new Error('No refresh token available');
        }

        // Prevent multiple simultaneous refresh requests
        if (this._isRefreshing) {
            return new Promise((resolve, reject) => {
                this._refreshSubscribers.push({ resolve, reject });
            });
        }

        this._isRefreshing = true;

        try {
            const client = axios.create({
                baseURL: urlJoin(this._apiEndpoint, this._apiVersion),
            });

            const response = await client.post('auth/learner/refresh', {
                refreshToken: this._tokenPair.refreshToken,
            });

            const { accessToken, refreshToken, expiresIn } = response.data;
            
            const expiresAt = new Date(Date.now() + expiresIn * 1000);
            const newTokenPair = new PlatformTokenPair({
                accessToken,
                refreshToken: refreshToken || this._tokenPair.refreshToken,
                expiresAt,
            });

            this._tokenPair = newTokenPair;

            // Notify all waiting subscribers
            this._refreshSubscribers.forEach(({ resolve }) => resolve(newTokenPair));
            this._refreshSubscribers = [];

            return newTokenPair;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] Failed to refresh access token`, error);
            
            // Notify all waiting subscribers of the error
            this._refreshSubscribers.forEach(({ reject }) => reject(error));
            this._refreshSubscribers = [];

            throw error;
        } finally {
            this._isRefreshing = false;
        }
    }

    /**
     * Overrides api version, useful for testing.
     * @param {string} apiVersion 
     */
    overrideApiVersion(apiVersion) {
        this._apiVersion = apiVersion;
    }

    get relativeLearnersRoute() {
        return `learners/${this._platformUserModel.id}`;
    }

    /**
     * Get current learner's name.
     * @returns {Promise<string>}
     */
    async getName() {
        const schema = {
            "type": "object",
            "properties": {
                "name": { "type": "string" },
            },
            "required": ["name"],
        };

        const response = await this._sendGetRequest(`${this.relativeLearnersRoute}/name`, { expectedResponseSchema: schema });
        return response.data.name;
    }

    /**
     * Put a score for the current learner.
     * @param {Object} params
     * @param {string} params.groupSpirit 
     * @param {string} params.trust
     */
    async putScore({ groupSpirit, trust }) {
        try {
            await this._sendPutRequest(`${this.relativeLearnersRoute}/score`, {
                groupSpirit: groupSpirit,
                trust: trust,
            });
        } catch (ex) {
            if (ex.response && ex.response.status === 409) {
                console.warn(`[${PlatformUserService.name}] Score already exists`);
                return;
            }

            console.error(`[${PlatformUserService.name}] Failed to put score`, ex);
            throw ex;
        }
    }

    /**
     * Get completion status for the current learner.
     * @returns {Promise<{status: "not started"|"incompleted"|"completed"}>}
     */
    async getCompletionStatus() {
        const schema = {
            "type": "object",
            "properties": {
                "status": { "type": "string", "enum": ["not_started", "incomplete", "completed"] },
            },
            "required": ["status"],
        };

        try {
            const response = await this._sendGetRequest(`${this.relativeLearnersRoute}/completion-status`, { expectedResponseSchema: schema });
            return response.data.status;
        } catch (ex) {
            if (ex.response && ex.response.status === 404) {
                console.warn(`[${PlatformUserService.name}] Completion status not found, defaulting to "not started"`);
                return PlatformUserModel.COMPLETION_STATUS.NOT_STARTED;
            }

            console.error(`[${PlatformUserService.name}] Failed to get completion status`, ex);
            throw ex;
        }
    }

    /**
     * Set completion status for the current learner.
     * @param {Object} params
     * @param {"not_started"|"incompleted"|"completed"} params.completionStatus
     */
    async setCompletionStatus({ completionStatus }) {
        await this._sendPutRequest(`${this.relativeLearnersRoute}/completion-status`, {
            status: completionStatus,
        });
    }

    /**
     * Read savegame data for the current learner
     * @returns {Promise<{savegameId: string, data: Object, version: string, createdAt: string, updatedAt: string}>}
     */
    async getSavegame() {
        const schema = {
            "type": "object",
            "properties": {
                "savegame": { "type": "string" },
            },
            "required": ["savegame"],
        };

        try {
            const response = await this._sendGetRequest(`${this.relativeLearnersRoute}/savegame`, { expectedResponseSchema: schema });
            return response.data.savegame;
        } catch (ex) {
            if (ex.response && ex.response.status === 404) {
                console.warn(`[${PlatformUserService.name}] Savegame not found`);
                return null;
            }

            console.error(`[${PlatformUserService.name}] Failed to get savegame`, ex);
            throw ex;
        }
    }

    /**
     * Set savegame data for the current learner.
     * @param {Object} params
     * @param {Object} params.savegame The savegame data object (serialized JSON)
     */
    async setSavegame({ savegame }) {
        await this._sendPutRequest(`${this.relativeLearnersRoute}/savegame`, {
            savegame: savegame,
        });
    }

    /**
     * Delete savegame data for the current learner.
     */
    async deleteSavegame() {
        await this._sendDeleteRequest(`${this.relativeLearnersRoute}/savegame`);
    }

    /**
     * Puts a conversation summary.
     * @param {Object} params
     * @param {string} params.conversationId Unique conversation identifier.
     * @param {string} params.summary Summary text.
     * @param {Array<{quote: string, feedback: string}>} params.feedbackItems Feedback items from the conversation.
     */
    async putConversationResult({ conversationId, summary, feedbackItems }) {
        try {
            await this._sendPutRequest(`${this.relativeLearnersRoute}/conversations/${conversationId}/result`, {
                summary: summary,
                feedbackItems: feedbackItems,
            });
        } catch (ex) {
            if (ex.response && ex.response.status === 409) {
                console.warn(`[${PlatformUserService.name}] Conversation result already exists for conversationId ${conversationId}`);
                return;
            }

            console.error(`[${PlatformUserService.name}] Failed to put conversation result for conversationId ${conversationId}`, ex);
            throw ex;
        }
    }

    /**
     * Puts a reflection summary.
     * @param {Object} params
     * @param {string} params.reflectionId Unique reflection identifier.
     * @param {string} params.summary Summary text.
     */
    async putReflectionResult({ reflectionId, summary }) {
        try {
            await this._sendPutRequest(`${this.relativeLearnersRoute}/reflections/${reflectionId}/result`, {
                summary: summary,
            });
        } catch (ex) {
            if (ex.response && ex.response.status === 409) {
                console.warn(`[${PlatformUserService.name}] Reflection result already exists for reflectionId ${reflectionId}`);
                return;
            }

            console.error(`[${PlatformUserService.name}] Failed to put reflection result for reflectionId ${reflectionId}`, ex);
            throw ex;
        }
    }

    async patchCompleteGameSession({ sessionId }) {
        try {
            await this._sendPatchRequest(`game-sessions/${sessionId}/complete`);
        } catch (ex) {
            console.error(`[${PlatformUserService.name}] Failed to complete game session`, ex);
            throw ex;
        }
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {boolean} params.failSilently
     * @return {Promise<import("axios").AxiosResponse<any, any>>}
     */
    async _sendGetRequest(endpointUrl, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createGameApiClient({ expectedResponseSchema, failSilently });
            const response = await client.get(endpointUrl);
            return response;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] GET ${endpointUrl}`, error);
            if (!failSilently) {
                throw error;
            }
        }
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} requestBody
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {boolean} params.failSilently
     * @return {Promise<import("axios").AxiosResponse<any, any>>}
     */
    async _sendPostRequest(endpointUrl, requestBody, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createGameApiClient({ expectedResponseSchema, failSilently });
            const response = await client.post(endpointUrl, requestBody);
            return response;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] POST ${endpointUrl}`, error);
            if (!failSilently) {
                throw error;
            }
        }
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} requestBody
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {boolean} params.failSilently
     * @return {Promise<import("axios").AxiosResponse<any, any>>}
     */
    async _sendPutRequest(endpointUrl, requestBody, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createGameApiClient({ expectedResponseSchema, failSilently });
            const response = await client.put(endpointUrl, requestBody);
            return response;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] PUT ${endpointUrl}`, error);
            if (!failSilently) {
                throw error;
            }
        }
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {boolean} params.failSilently
     * @return {Promise<import("axios").AxiosResponse<any, any>>}
     */
    async _sendDeleteRequest(endpointUrl, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createGameApiClient({ expectedResponseSchema, failSilently });
            const response = await client.delete(endpointUrl);
            return response;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] DELETE ${endpointUrl}`, error);
            if (!failSilently) {
                throw error;
            }
        }
    }

    async _sendPatchRequest(endpointUrl, requestBody, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createGameApiClient({ expectedResponseSchema, failSilently });
            const response = await client.patch(endpointUrl, requestBody);
            return response;
        } catch (error) {
            console.error(`[${PlatformUserService.name}] PATCH ${endpointUrl}`, error);
            if (!failSilently) {
                throw error;
            }
        }
    }

    /**
     * @param {Object} param
     * @param {*} param.expectedResponseSchema
     * @param {boolean} param.failSilently
     * @returns {import("axios").AxiosInstance}
     */
    _createGameApiClient({ expectedResponseSchema, failSilently } = {}) {
        const client = axios.create({
            baseURL: urlJoin(this._apiGameEndpoint, this._apiVersion),
            headers: {
                Authorization: `Bearer ${this._tokenPair.accessToken}`,
            },
        });

        // Request interceptor: refresh token if it's about to expire
        client.interceptors.request.use(
            async (config) => {
                // Check if token expires in the next 5 minutes
                if (this._tokenPair && this._tokenPair.expiresIn(5 * 60 * 1000)) {
                    try {
                        await this.refreshAccessToken();
                        config.headers.Authorization = `Bearer ${this._tokenPair.accessToken}`;
                    } catch (error) {
                        console.error(`[${PlatformUserService.name}] Failed to refresh token in request interceptor`, error);
                    }
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor: refresh token on 401 and retry
        client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // If 401 and we haven't retried yet, try to refresh
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        await this.refreshAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${this._tokenPair.accessToken}`;
                        return client(originalRequest);
                    } catch (refreshError) {
                        console.error(`[${PlatformUserService.name}] Failed to refresh token on 401`, refreshError);
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );

        axiosRetry(client, {
            retries: 3,
            retryDelay: axiosRetry.exponentialDelay,
            onRetry: (retryCount, error) => {
                console.warn(`[${PlatformUserService.name}] Retry attempt ${retryCount}`, error);
            },
            onMaxRetryTimesExceeded: (error, retryCount) => {
                console.error(`[${PlatformUserService.name}] Max retry times (${retryCount}) exceeded`, error);

                if (!failSilently) {
                    this._dispatcher?.dispatch('platform-error', { error, retryCount });
                }
            },
            retryCondition: (error) => {
                // Retry for network errors or idempotent requests.
                if (axiosRetry.isNetworkOrIdempotentRequestError(error)) {
                    return true;
                }

                // Don't retry for client errors.
                if (error.status >= 400 && error.status < 500) {
                    return false;
                }

                // Schema validation - if the response does not match the schema, retry.
                if (expectedResponseSchema && error.response && error.response.data) {
                    const ajv = new Ajv();
                    const validateSchema = ajv.compile(expectedResponseSchema);
                    const isValidSchema = validateSchema(error.response.data);
                    if (!isValidSchema) {
                        console.error(`[${PlatformUserService.name}] Response did not match schema:`, validateSchema.errors);
                        return true;
                    }
                }

                // Do not retry otherwise.
                if (!failSilently && error.response) {
                    this._dispatcher?.dispatch('platform-error', { error });
                }
                return false;
            },
            validateResponse: (response) => {
                // Validate the response against the schema.
                if (expectedResponseSchema) {
                    const ajv = new Ajv();
                    const validateSchema = ajv.compile(expectedResponseSchema);
                    const isValidSchema = validateSchema(response.data);
                    if (!isValidSchema) {
                        console.error(`[${PlatformUserService.name}] Response did not match schema:`, validateSchema.errors);
                        return false;
                    }
                }

                // 2XX responses are valid.
                const status = response.status;
                if (status >= 200 && status < 300) {
                    return true;
                }

                return false;
            }
        });

        return client;
    }
}

export const HTTPMethod = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
};
